import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import Styles from "./SelfListing.module.css";
import ReceivedModal from "../../../Modals/Banking/Received/ReceivedModal";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  fetchAllBankingListings,
  fetchDocumentUrlByLedgerId,
  fetchMatchmakingResults,
  setActiveListingId,
  clearMatchmakingData,
} from "../../../../Redux/Slices/bankingSlice";
import ViewInjectDrawal from "../../../Modals/Banking/ViewInjectDrawal/ViewInjectDrawal";
import SelfListingBox from "./SelfListingBox";
import BankingListingModal from "../../../Modals/BankingListingModal/BankingListingModal";
import AvailableListingSkeleton from "../../../../commons/AvailableListingSkeleton";
import NoDataPage from "../../NoDataPage/NoDataPage";
import PreviewModal from "../../../../commons/PreviewModal/PreviewModal";

const SelfListing = ({ onFilterChange }) => {
  const [createEditModal, setCreateEditModal] = useState(false);
  const [received, setReceived] = useState(false);
  const [indentsModal, setIndentsModal] = useState(false);
  const [duplicateModal, setDuplicateModal] = useState(false);
  const [termsFile, setTermsFile] = useState(null);
  const [termsFileMap, setTermsFileMap] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);

  const scrollPositionRef = useRef(0);

  const listingRefs = useRef({});

  const handleClose = () => setCreateEditModal(false);
  const dispatch = useDispatch();
  const bankingState = useSelector((state) => state?.bankingSlice || {});
  const allListings = useSelector(
    (state) => state.bankingSlice.allListings,
    shallowEqual,
  );

  const FILTER_KEY = "selfListingActiveFilter";
  const [activeFilter, setActiveFilter] = useState(() => {
    return sessionStorage.getItem(FILTER_KEY) || "ALL";
  });

  const activeListingId = useSelector(
    (state) => state.bankingSlice.activeListingId,
  );
  const activeListing = allListings.find(
    (listing) => listing.id === activeListingId,
  );
  const loading = useSelector(
    (state) =>
      state.bankingSlice.loading && state.bankingSlice.allListings.length === 0,
    shallowEqual,
  );

  const handleActiveList = useCallback(
    (id) => {
      if (id !== activeListingId) {
        dispatch(setActiveListingId(id));
      }
    },
    [dispatch, activeListingId],
  );

  const SCROLL_POS_KEY = "selfListingScrollPos";
  useEffect(() => {
    const savedScrollPos = sessionStorage.getItem(SCROLL_POS_KEY);
    if (savedScrollPos) {
      window.scrollTo(0, parseInt(savedScrollPos, 10));
      sessionStorage.removeItem(SCROLL_POS_KEY);
    }
  }, []);

  useEffect(() => {
    if (!activeListingId) {
      const firstId = allListings?.[0]?.id;
      if (firstId) {
        handleActiveList(firstId);
      }
    }
  }, [allListings, activeListingId, handleActiveList]);

  useEffect(() => {
    if (activeListingId && allListings.length > 0 && !loading) {
      const timer = setTimeout(() => {
        if (listingRefs.current[activeListingId]) {
          listingRefs.current[activeListingId].scrollIntoView({
            behavior: "auto",
            block: "center",
          });
        }
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [activeListingId, allListings.length, loading]);

  const handleOpenIndent = (data) => {
    sessionStorage.setItem(SCROLL_POS_KEY, window.scrollY.toString());
    dispatch(setActiveListingId(data.id));
    setIndentsModal(true);
  };
  const handleCloseIndent = () => {
    setIndentsModal(false);
  };

  const restoreScrollPosition = () => {
    if (scrollPositionRef.current) {
      window.scrollTo(0, scrollPositionRef.current);
    }
  };

  useEffect(() => {
    restoreScrollPosition();
  }, []);

  const handleOpenDup = () => {
    setDuplicateModal(true);
  };

  const handleCloseDup = () => {
    setDuplicateModal(false);
  };

  const openEditModalWithId = (id) => {
    handleActiveList(id);
    setCreateEditModal(true);
  };

  const refreshData = useCallback(() => {
    dispatch(fetchAllBankingListings());
  }, [dispatch]);

  const normalizeStatus = (status) => {
    if (!status) return "";
    const s = status.toUpperCase();
    if (s === "TERMS_ACCEPTED") return "ACCEPTED";
    if (s === "COMPLETED") return "ONGOING";
    return s;
  };

  const filteredListings = useMemo(() => {
    return allListings.filter((item) => {
      if (activeFilter === "ALL") return true;
      const itemStatus = normalizeStatus(item.status);
      return itemStatus === activeFilter;
    });
  }, [allListings, activeFilter]);

  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(activeFilter, filteredListings);
    }
  }, [activeFilter, filteredListings]);

  const handleFilterClick = (filterName) => {
    setActiveFilter(filterName);
    sessionStorage.setItem(FILTER_KEY, filterName);

    dispatch(clearMatchmakingData());

    dispatch(fetchAllBankingListings());

    const filtered =
      filterName === "ALL"
        ? allListings
        : allListings.filter(
            (item) => normalizeStatus(item.status) === filterName,
          );

    if (filtered.length) {
      const isActiveListingInFiltered = filtered.some(
        (item) => item.id === activeListingId,
      );
      const listingIdToSet = isActiveListingInFiltered
        ? activeListingId
        : filtered[0].id;

      handleActiveList(listingIdToSet);

      dispatch(
        fetchMatchmakingResults({
          id: listingIdToSet,
          statusFilter: filterName === "ALL" ? null : filterName,
        }),
      );
    } else {
      dispatch(setActiveListingId(null));
    }
  };

  useEffect(() => {
    if (filteredListings.length === 0) {
      dispatch(setActiveListingId(null));
    }
  }, [filteredListings, dispatch]);

  const onViewFile = async (docId) => {
    if (!docId) return;
    try {
      const resultAction = await dispatch(fetchDocumentUrlByLedgerId(docId));
      if (fetchDocumentUrlByLedgerId.fulfilled.match(resultAction)) {
        const url = resultAction.payload?.url || resultAction.payload;
        if (url) {
          setPreviewFileUrl(url);
          setPreviewOpen(true);
        } else {
        }
      } else {
      }
    } catch (err) {}
  };

  return (
    <>
      {createEditModal && (
        <BankingListingModal
          open={createEditModal}
          handleClose={handleClose}
          mode="edit"
          existingListing={activeListing}
          activeListingId={activeListingId}
          refreshData={refreshData}
          heading="Edit Banking Listing"
          onUploadSuccess={(fileObj) => setTermsFile(fileObj)}
        />
      )}

      {duplicateModal && (
        <BankingListingModal
          open={duplicateModal}
          handleClose={handleCloseDup}
          mode="duplicate"
          existingListing={activeListing}
          heading="Duplicate Banking Listing"
          onUploadSuccess={(fileObj) => setTermsFile(fileObj)}
        />
      )}
      {received && (
        <ReceivedModal
          key={activeListingId}
          open={received}
          handleClose={() => setReceived(false)}
          listingName={activeListing?.name || "—"}
          listingId={activeListingId}
        />
      )}
      {indentsModal && (
        <ViewInjectDrawal
          open={indentsModal}
          handleClose={handleCloseIndent}
          data={activeListing ? [activeListing] : []}
          activeListingId={activeListingId}
          termsDocumentId={activeListing?.termsDocumentId}
          onViewDocument={onViewFile}
        />
      )}
      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fileUrl={previewFileUrl}
      />
      <div className={Styles.sortSection}>
        {["ALL", "ACTIVE", "HOLD", "ACCEPTED", "ONGOING"].map((filter) => (
          <div
            key={filter}
            className={`${Styles.filterButton} ${
              activeFilter === filter ? Styles.activeFilter : ""
            }`}
            onClick={() => handleFilterClick(filter)}
          >
            {filter.charAt(0) + filter.slice(1).toLowerCase()}
          </div>
        ))}
      </div>

      <>
        {loading ? (
          <AvailableListingSkeleton count={4} />
        ) : filteredListings.length === 0 ? (
          <div className={Styles.noDataMessage}>
            <NoDataPage isInSelfListing={true} />
          </div>
        ) : (
          <>
            {filteredListings?.map((item) => (
              <div
                key={item.id}
                ref={(el) => {
                  if (el) listingRefs.current[item.id] = el;
                  else delete listingRefs.current[item.id];
                }}
              >
                <SelfListingBox
                  key={item.id}
                  item={item}
                  handleOpenIndent={handleOpenIndent}
                  setCreateEditModal={setCreateEditModal}
                  handleOpenDup={handleOpenDup}
                  setDuplicateModal={setDuplicateModal}
                  activeListingId={activeListingId === item.id}
                  alterButton={true}
                  onEditClick={() => openEditModalWithId(item.id)}
                  onDuplicateClick={() => {
                    handleActiveList(item.id);
                    handleOpenDup();
                  }}
                  termsFile={termsFileMap[item.id] || termsFile}
                  termsDocumentId={
                    termsFileMap[item.id]?.id || item.termsDocumentId
                  }
                  onViewFile={onViewFile}
                />
              </div>
            ))}
          </>
        )}
      </>
    </>
  );
};

export default SelfListing;
